import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Divider,
  Card,
  CardContent,
  Avatar
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AppHeader from "../../components/AppHeader";
import { buildHierarchicalList } from "../../components/staff/constants";
import { fetchLeaveCalendar } from "../../services/webReportsService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { myanmarFontBase64 } from "../../utils/myanmarFontBase64";

// Calendar helper names
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Color mapping for leave types
const getLeaveTypeColor = (typeCode) => {
  const code = (typeCode || "").toUpperCase();
  if (code.includes("AL") || code.includes("ANNUAL")) return { bg: "#e0f2fe", text: "#0369a1" }; // Blue
  if (code.includes("CL") || code.includes("CASUAL")) return { bg: "#fef3c7", text: "#b45309" }; // Amber
  if (code.includes("SL") || code.includes("SICK")) return { bg: "#fee2e2", text: "#b91c1c" }; // Red
  if (code.includes("ML") || code.includes("MATERNITY")) return { bg: "#f3e8ff", text: "#6b21a8" }; // Purple
  return { bg: "#f1f5f9", text: "#475569" }; // Slate/Grey default
};

export default function LeaveCalendar({ setIsLoggedIn }) {
  const navigate = useNavigate();
  const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
  const token = useMemo(() => sessionStorage.getItem("token") || "", []);

  // Filter and Calendar Date States
  const [currentDate, setCurrentDate] = useState(new Date());
  const [branchId, setBranchId] = useState("");
  const [orgNodeId, setOrgNodeId] = useState("");

  // Metadata dropdown options
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateMap, setDateMap] = useState({});

  // Drawer details panel states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDateStr, setSelectedDateStr] = useState("");

  // Helper variables for year range selection
  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const years = [];
    for (let y = cy - 3; y <= cy + 3; y++) {
      years.push(y);
    }
    return years;
  }, []);

  const monthStr = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = String(currentDate.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  }, [currentDate]);

  // Fetch branches and departments list on load
  useEffect(() => {
    const fetchMetadata = async () => {
      const config = {
        withCredentials: true,
        headers: {
          "X-Tenant": tenant,
          "Authorization": `Bearer ${token}`
        }
      };
      try {
        const [branchRes, nodeRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
          axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
        ]);

        if (Array.isArray(branchRes.data?.data)) setBranches(branchRes.data.data);
        if (Array.isArray(nodeRes.data?.data)) setDepartments(buildHierarchicalList(nodeRes.data.data));
      } catch (err) {
        console.error("Failed to load metadata filters:", err);
      }
    };

    if (tenant && token) {
      fetchMetadata();
    }
  }, [tenant, token]);

  // Fetch calendar leaves representation
  const loadCalendarData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchLeaveCalendar({
        month: monthStr,
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined
      });

      if (res.success && res.data) {
        setDateMap(res.data.dateMap || {});
      } else {
        throw new Error(res.message || "Failed to load leave calendar records");
      }
    } catch (err) {
      console.error("Error loading leave calendar:", err);
      setError(err.message || "Failed to load leave calendar records.");
      setDateMap({});
    } finally {
      setLoading(false);
    }
  }, [monthStr, branchId, orgNodeId]);

  useEffect(() => {
    if (tenant) {
      loadCalendarData();
    }
  }, [tenant, loadCalendarData]);

  // Month navigation logic
  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleMonthSelectChange = (e) => {
    const m = parseInt(e.target.value, 10);
    setCurrentDate(prev => new Date(prev.getFullYear(), m, 1));
  };

  const handleYearSelectChange = (e) => {
    const y = parseInt(e.target.value, 10);
    setCurrentDate(prev => new Date(y, prev.getMonth(), 1));
  };

  // Calendar cells computation
  const firstDayOfWeekIndex = useMemo(() => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  }, [currentDate]);

  const totalDaysInMonth = useMemo(() => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  }, [currentDate]);

  const calendarDays = useMemo(() => {
    const days = [];
    // Prepend empty slots for alignment
    for (let i = 0; i < firstDayOfWeekIndex; i++) {
      days.push(null);
    }
    // Add all calendar month dates
    for (let d = 1; d <= totalDaysInMonth; d++) {
      days.push(d);
    }
    return days;
  }, [firstDayOfWeekIndex, totalDaysInMonth]);

  // Check if a day is today
  const isToday = useCallback((dayNum) => {
    if (!dayNum) return false;
    const today = new Date();
    return today.getFullYear() === currentDate.getFullYear() &&
           today.getMonth() === currentDate.getMonth() &&
           today.getDate() === dayNum;
  }, [currentDate]);

  // Click on a cell opens detail drawer
  const handleDayClick = (dayNum) => {
    if (!dayNum) return;
    const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    setSelectedDateStr(dateKey);
    setDrawerOpen(true);
  };

  // Retrieve data of selected day
  const selectedDayData = useMemo(() => {
    if (!selectedDateStr) return { count: 0, leaves: [] };
    return dateMap[selectedDateStr] || { count: 0, leaves: [] };
  }, [selectedDateStr, dateMap]);

  // Format date display (e.g. Wednesday, Jun 10, 2026)
  const formatFriendlyDate = (dateKey) => {
    if (!dateKey) return "";
    const [y, m, d] = dateKey.split("-").map(Number);
    const dateObj = new Date(y, m - 1, d);
    return dateObj.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "short", day: "numeric" });
  };

  // Daily away list PDF export
  const exportDailyPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      if (myanmarFontBase64) {
        doc.addFileToVFS("Padauk-Regular.ttf", myanmarFontBase64);
        doc.addFont("Padauk-Regular.ttf", "Padauk", "normal");
        doc.addFont("Padauk-Regular.ttf", "Padauk", "bold");
      }
      const defaultFont = myanmarFontBase64 ? "Padauk" : "helvetica";

      // Header configuration
      doc.setFont(defaultFont, "bold");
      doc.setFontSize(16);
      doc.setTextColor(30, 41, 59); // slate-800
      doc.text("Daily Leave Log", 14, 15);

      doc.setFont(defaultFont, "normal");
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date: ${formatFriendlyDate(selectedDateStr)}`, 14, 21);
      doc.text(`Total Staff on Leave: ${selectedDayData.count} (${selectedDayData.approvedCount || 0} Approved, ${selectedDayData.pendingCount || 0} Pending)`, 14, 26);

      const tableHeaders = ["No.", "Employee ID", "Staff Name", "Department", "Leave Type", "Duration", "Status", "Reason"];
      const tableRows = selectedDayData.leaves.map((leaf, index) => [
        index + 1,
        leaf.employee_id || "-",
        leaf.full_name,
        leaf.dept_name || "-",
        leaf.type_name,
        leaf.duration,
        leaf.status ? leaf.status.toUpperCase() : "-",
        leaf.reason || "-"
      ]);

      autoTable(doc, {
        startY: 32,
        head: [tableHeaders],
        body: tableRows,
        theme: "striped",
        headStyles: { fillColor: [79, 70, 229], font: defaultFont, fontStyle: "bold" },
        styles: { fontSize: 8, font: defaultFont },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 20 },
          2: { cellWidth: 30 },
          3: { cellWidth: 30 },
          4: { cellWidth: 25 },
          5: { cellWidth: 20 },
          6: { cellWidth: 20 },
          7: { cellWidth: 35 }
        }
      });

      doc.save(`Leave_Log_${selectedDateStr}.pdf`);
    } catch (e) {
      alert("Failed to export PDF: " + e.message);
    }
  };

  // Daily away list CSV export
  const exportDailyCSV = () => {
    try {
      const headers = ["No.", "Employee ID", "Staff Name", "Department", "Branch", "Leave Type", "Duration", "Status", "Reason"];
      const rows = selectedDayData.leaves.map((leaf, index) => [
        index + 1,
        `"${leaf.employee_id || ""}"`,
        `"${leaf.full_name.replace(/"/g, '""')}"`,
        `"${(leaf.dept_name || "").replace(/"/g, '""')}"`,
        `"${(leaf.branch_name || "").replace(/"/g, '""')}"`,
        `"${leaf.type_name.replace(/"/g, '""')}"`,
        `"${leaf.duration.replace(/"/g, '""')}"`,
        `"${(leaf.status || "").toUpperCase()}"`,
        `"${(leaf.reason || "").replace(/"/g, '""')}"`
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map(e => e.join(","))
      ].join("\n");

      // Generate downloadable blob
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" }); // include UTF-8 BOM
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Leave_Log_${selectedDateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      alert("Failed to export CSV: " + e.message);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />

      <Box sx={{ p: 4, maxWidth: 1400, mx: "auto" }}>
        {/* Header Block */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <IconButton onClick={() => navigate("/reports")} sx={{ color: "#64748b" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 900, color: "#0f172a" }}>
            Leave Calendar
          </Typography>
        </Stack>
        <Typography variant="body1" sx={{ color: "#64748b", mb: 4, ml: 6 }}>
          Visual representation of approved leave requests by month. Click on any date cell to view detailed roster logs.
        </Typography>

        {/* Filter Controls Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 4,
            border: "1px solid #e2e8f0",
            display: "flex",
            flexWrap: "wrap",
            gap: 2.5,
            alignItems: "center",
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)"
          }}
        >
          {/* Calendar Jump selectors */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="month-select-label">Month</InputLabel>
              <Select
                labelId="month-select-label"
                value={currentDate.getMonth()}
                label="Month"
                onChange={handleMonthSelectChange}
              >
                {MONTHS.map((m, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="year-select-label">Year</InputLabel>
              <Select
                labelId="year-select-label"
                value={currentDate.getFullYear()}
                label="Year"
                onChange={handleYearSelectChange}
              >
                {yearOptions.map(y => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

          {/* Org & Branch Filters */}
          <Stack direction="row" spacing={2} sx={{ flexGrow: 1, minWidth: 300 }} flexWrap="wrap" gap={1.5}>
            <FormControl size="small" sx={{ minWidth: 180, flexGrow: { xs: 1, sm: 0 } }}>
              <InputLabel id="branch-select-label">Branch</InputLabel>
              <Select
                labelId="branch-select-label"
                value={branchId}
                label="Branch"
                onChange={(e) => setBranchId(e.target.value)}
              >
                <MenuItem value="">All Branches</MenuItem>
                {branches.map(b => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.branch_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200, flexGrow: { xs: 1, sm: 0 } }}>
              <InputLabel id="dept-select-label">Department</InputLabel>
              <Select
                labelId="dept-select-label"
                value={orgNodeId}
                label="Department"
                onChange={(e) => setOrgNodeId(e.target.value)}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map(d => (
                  <MenuItem key={d.id} value={d.id} sx={{ pl: 2 + (d._depth || 0) * 2 }}>
                    {d._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                    {d.node_name}{d.level_type ? ` (${d.level_type})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh dataset">
              <IconButton onClick={loadCalendarData} disabled={loading} sx={{ border: "1px solid #cbd5e1" }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {/* Calendar visual board */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 5,
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.03), 0 4px 6px -4px rgba(0,0,0,0.03)"
          }}
        >
          {/* Month Grid Header - Controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              borderBottom: "1px solid #e2e8f0",
              bgcolor: "#ffffff"
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CalendarIcon sx={{ color: "primary.main", fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <IconButton onClick={handlePrevMonth} sx={{ border: "1px solid #cbd5e1" }}>
                <ChevronLeftIcon />
              </IconButton>
              <Button
                variant="outlined"
                onClick={() => setCurrentDate(new Date())}
                sx={{ borderRadius: 2, borderColor: "#cbd5e1", color: "#475569", fontWeight: 700 }}
              >
                Today
              </Button>
              <IconButton onClick={handleNextMonth} sx={{ border: "1px solid #cbd5e1" }}>
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Weekdays Labels */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              textAlign: "center"
            }}
          >
            {WEEKDAYS.map((day, idx) => (
              <Box key={idx} sx={{ py: 1.5, borderRight: idx < 6 ? "1px solid #e2e8f0" : "none" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color: day === "Sunday" || day === "Saturday" ? "#ef4444" : "#475569",
                    fontSize: "0.82rem",
                    textTransform: "uppercase"
                  }}
                >
                  {day.substring(0, 3)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Monthly grid calendar cells */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 380, bgcolor: "#ffffff" }}>
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={45} thickness={4} sx={{ color: "primary.main" }} />
                <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
                  Retrieving leave metrics...
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Fade in={!loading}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  bgcolor: "#cbd5e1", // lines color between cells
                  gap: "1px",
                  p: 0
                }}
              >
                {calendarDays.map((dayNum, cellIdx) => {
                  const dayOfWeek = cellIdx % 7;
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                  let dateKey = "";
                  let dayLeaves = { count: 0, leaves: [] };

                  if (dayNum) {
                    dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
                    dayLeaves = dateMap[dateKey] || { count: 0, leaves: [] };
                  }

                  const activeCell = dayNum !== null;
                  const isCellToday = isToday(dayNum);
                  const isCellSelected = selectedDateStr === dateKey && drawerOpen;

                  return (
                    <Box
                      key={cellIdx}
                      onClick={() => activeCell && handleDayClick(dayNum)}
                      sx={{
                        minHeight: 110,
                        bgcolor: activeCell ? "#ffffff" : "#f1f5f9",
                        p: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        cursor: activeCell ? "pointer" : "default",
                        boxSizing: "border-box",
                        position: "relative",
                        transition: "all 0.15s ease-in-out",
                        border: isCellSelected ? "2px solid #6366f1" : "none",
                        "&:hover": activeCell ? {
                          bgcolor: "#f8fafc",
                          boxShadow: "inset 0 0 10px rgba(0,0,0,0.02)",
                          transform: "translateY(-1px)",
                          zIndex: 2
                        } : {}
                      }}
                    >
                      {/* Day count number */}
                      {dayNum && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          {isCellToday ? (
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: "primary.main",
                                color: "#ffffff",
                                fontSize: "0.85rem",
                                fontWeight: 700
                              }}
                            >
                              {dayNum}
                            </Avatar>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 700,
                                color: isWeekend ? "#f87171" : "#334155"
                              }}
                            >
                              {dayNum}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Display count pills if leaves are active */}
                      {dayNum && (dayLeaves.approvedCount > 0 || dayLeaves.pendingCount > 0) && (
                        <Box sx={{ mt: "auto", textAlign: "center", display: "flex", flexDirection: "column", gap: 0.5 }}>
                          {dayLeaves.approvedCount > 0 && (
                            <Chip
                              label={`${dayLeaves.approvedCount} approved`}
                              size="small"
                              sx={{
                                width: "100%",
                                borderRadius: 1.5,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                bgcolor: "#e0f5ec",
                                color: "#0e7490",
                                border: "1px solid #a7f3d0",
                                "& .MuiChip-label": { px: 0.5, py: 0 }
                              }}
                            />
                          )}
                          {dayLeaves.pendingCount > 0 && (
                            <Chip
                              label={`${dayLeaves.pendingCount} pending`}
                              size="small"
                              sx={{
                                width: "100%",
                                borderRadius: 1.5,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                bgcolor: "#fef3c7",
                                color: "#d97706",
                                border: "1px solid #fde68a",
                                "& .MuiChip-label": { px: 0.5, py: 0 }
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Fade>
          )}
        </Paper>
      </Box>

      {/* Slide-out details drawer panel */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 550, md: 700 },
            p: 4,
            boxSizing: "border-box",
            borderLeft: "1px solid #e2e8f0",
            boxShadow: "-10px 0 30px rgba(0,0,0,0.04)"
          }
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CalendarIcon sx={{ color: "primary.main", fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b" }}>
              Leave Log details
            </Typography>
          </Stack>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ border: "1px solid #e2e8f0" }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Typography variant="body1" sx={{ fontWeight: 800, color: "#334155", mb: 0.5 }}>
          {formatFriendlyDate(selectedDateStr)}
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mb: 3 }}>
          {selectedDayData.count} staff members on leave ({selectedDayData.approvedCount || 0} approved, {selectedDayData.pendingCount || 0} pending).
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Actions Bar */}
        {selectedDayData.count > 0 && (
          <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PdfIcon />}
              onClick={exportDailyPDF}
              sx={{ borderRadius: 2, borderColor: "#cbd5e1", color: "#475569", fontWeight: 700, textTransform: "none" }}
            >
              Export PDF
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={exportDailyCSV}
              sx={{ borderRadius: 2, borderColor: "#cbd5e1", color: "#475569", fontWeight: 700, textTransform: "none" }}
            >
              Export CSV
            </Button>
          </Stack>
        )}

        {/* Detailed roster list */}
        {selectedDayData.count === 0 ? (
          <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <CalendarIcon sx={{ fontSize: 60, color: "#cbd5e1", mb: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#64748b", mb: 0.5 }}>
              No Leaves Logged
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              All staff members are expected on active duty for this date.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 800, py: 1.5 }}>Staff Member</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 1.5 }}>Dept & Branch</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 1.5 }}>Leave Type</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 1.5 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 1.5 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedDayData.leaves.map((leaf, index) => {
                  const colorConfig = getLeaveTypeColor(leaf.type_code);
                  return (
                    <React.Fragment key={index}>
                      <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                            {leaf.full_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {leaf.employee_id || "No ID"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            {leaf.dept_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                            {leaf.branch_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={leaf.type_name}
                            size="small"
                            sx={{
                              bgcolor: colorConfig.bg,
                              color: colorConfig.text,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              borderRadius: 1.5
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            {leaf.duration}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={(leaf.status || "").toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: leaf.status === "approved" ? "#e0f5ec" : "#fef3c7",
                              color: leaf.status === "approved" ? "#0e7490" : "#d97706",
                              fontWeight: 800,
                              fontSize: "0.72rem",
                              borderRadius: 1.5
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      {/* Reason row */}
                      {leaf.reason && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ bgcolor: "#fafafa", py: 1, borderTop: "none" }}>
                            <Typography variant="caption" sx={{ color: "#64748b", fontStyle: "italic", display: "block" }}>
                              Reason: {leaf.reason}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Drawer>
    </Box>
  );
}
